import React, { useState, useMemo } from 'react';
import RelationshipGraphTutorial from './RelationshipGraphTutorial';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  ButtonGroup,
  Slider,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  AccountTree as GraphIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Inventory as EvidenceIcon,
  FilterList as FilterIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as CenterIcon,
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon
} from '@mui/icons-material';
import { SceneObject, Evidence, Movement, AttributeLockState, AttributeFilters } from '../types';

interface RelationshipGraphProps {
  sceneObjects: SceneObject[];
  evidenceList: Evidence[];
  movements: Movement[];
  currentCaseId: string;
  attributeLock: AttributeLockState;
  attributeFilters: AttributeFilters;
  startTimeRange: number;
  endTimeRange: number;
  getTimestampFromSlider: (value: number) => number;
  onObjectClick: (obj: SceneObject | Evidence) => void;
}

interface GraphNode {
  id: string;
  label: string;
  type: 'person' | 'location' | 'evidence';
  data: SceneObject | Evidence | { name: string; type: string };
  x?: number;
  y?: number;
  size: number;
  color: string;
}

interface GraphLink {
  source: string;
  target: string;
  type: 'co-location' | 'movement' | 'evidence-location' | 'family' | 'case';
  strength: number;
  label?: string;
}

const RelationshipGraph: React.FC<RelationshipGraphProps> = ({
  sceneObjects,
  evidenceList,
  movements,
  currentCaseId,
  attributeLock,
  attributeFilters,
  startTimeRange,
  endTimeRange,
  getTimestampFromSlider,
  onObjectClick
}) => {
  const [graphType, setGraphType] = useState<'all' | 'people' | 'locations' | 'evidence'>('all');
  const [relationshipType, setRelationshipType] = useState<'all' | 'co-location' | 'movement' | 'evidence-location' | 'family'>('all');
  const [zoom, setZoom] = useState(1);
  const [showLabels, setShowLabels] = useState(true);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [simplifyMode, setSimplifyMode] = useState(false);
  const [minConnections, setMinConnections] = useState(1);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [showQuickStart, setShowQuickStart] = useState(true);

  // Filter data based on current case and time range
  const filteredSceneObjects = useMemo(() => {
    const startTime = getTimestampFromSlider(startTimeRange);
    const endTime = getTimestampFromSlider(endTimeRange);
    
    return sceneObjects.filter(obj => {
      if (obj.caseId !== currentCaseId) return false;
      
      const objTime = new Date(obj.time).getTime();
      if (objTime < startTime || objTime > endTime) return false;
      
      // Apply attribute filters
      if (attributeLock.attribute === 'people' && attributeLock.value) {
        return obj.people.includes(attributeLock.value);
      }
      if (attributeLock.attribute === 'location' && attributeLock.value) {
        const locationName = obj.location.global?.name || obj.location.local?.name;
        return locationName === attributeLock.value;
      }
      if (attributeLock.attribute === 'time' && attributeLock.value) {
        return new Date(obj.time).toDateString() === attributeLock.value;
      }
      
      return true;
    });
  }, [sceneObjects, currentCaseId, startTimeRange, endTimeRange, attributeLock, getTimestampFromSlider]);

  const filteredEvidence = useMemo(() => {
    const startTime = getTimestampFromSlider(startTimeRange);
    const endTime = getTimestampFromSlider(endTimeRange);
    
    return evidenceList.filter(evidence => {
      if (evidence.caseId !== currentCaseId) return false;
      
      const evidenceTime = new Date(evidence.time).getTime();
      return evidenceTime >= startTime && evidenceTime <= endTime;
    });
  }, [evidenceList, currentCaseId, startTimeRange, endTimeRange, getTimestampFromSlider]);

  // Generate graph nodes and links
  const { nodes, links } = useMemo(() => {
    const nodes: GraphNode[] = [];
    const links: GraphLink[] = [];
    const nodeMap = new Map<string, GraphNode>();

    // Add person nodes
    const uniquePeople = new Set<string>();
    filteredSceneObjects.forEach(obj => {
      obj.people.forEach(person => uniquePeople.add(person));
    });

    uniquePeople.forEach(person => {
      // Calculate importance based on number of appearances
      const appearances = filteredSceneObjects.filter(obj => obj.people.includes(person)).length;
      const size = Math.max(15, Math.min(30, 15 + appearances * 2));
      
      const node: GraphNode = {
        id: `person-${person}`,
        label: person.replace(/^(Detective|Person of Interest|Witness|Security Guard|Victim)\s+/, ''),
        type: 'person',
        data: { name: person, type: 'person' },
        size: size,
        color: '#4CAF50'
      };
      nodes.push(node);
      nodeMap.set(node.id, node);
    });

    // Add location nodes
    const uniqueLocations = new Set<string>();
    filteredSceneObjects.forEach(obj => {
      const locationName = obj.location.global?.name || obj.location.local?.name;
      if (locationName) uniqueLocations.add(locationName);
    });
    filteredEvidence.forEach(evidence => {
      const locationName = evidence.location.global?.name || evidence.location.local?.name;
      if (locationName) uniqueLocations.add(locationName);
    });

    uniqueLocations.forEach(location => {
      // Calculate importance based on number of people and evidence
      const peopleCount = filteredSceneObjects.filter(obj => 
        (obj.location.global?.name === location || obj.location.local?.name === location)
      ).length;
      const evidenceCount = filteredEvidence.filter(evidence => 
        (evidence.location.global?.name === location || evidence.location.local?.name === location)
      ).length;
      const size = Math.max(12, Math.min(25, 12 + (peopleCount + evidenceCount) * 1.5));
      
      const node: GraphNode = {
        id: `location-${location}`,
        label: location,
        type: 'location',
        data: { name: location, type: 'location' },
        size: size,
        color: '#2196F3'
      };
      nodes.push(node);
      nodeMap.set(node.id, node);
    });

    // Add evidence nodes
    filteredEvidence.forEach(evidence => {
      const node: GraphNode = {
        id: `evidence-${evidence.id}`,
        label: evidence.name,
        type: 'evidence',
        data: evidence,
        size: 12,
        color: '#FF9800'
      };
      nodes.push(node);
      nodeMap.set(node.id, node);
    });

         // Generate co-location links (people at same location at same time)
     const timeGroups = new Map<string, Map<string, string[]>>(); // time -> location -> people
     filteredSceneObjects.forEach(obj => {
       const timeKey = obj.time;
       const locationName = obj.location.global?.name || obj.location.local?.name;
       if (!locationName) return;
       
       if (!timeGroups.has(timeKey)) {
         timeGroups.set(timeKey, new Map());
       }
       if (!timeGroups.get(timeKey)!.has(locationName)) {
         timeGroups.get(timeKey)!.set(locationName, []);
       }
       timeGroups.get(timeKey)!.get(locationName)!.push(...obj.people);
     });

     timeGroups.forEach((locationMap, time) => {
       locationMap.forEach((people, location) => {
         // Only create links if there are 2-4 people (avoid too many connections)
         if (people.length >= 2 && people.length <= 4) {
           // Create links between all people at this location at this time
           for (let i = 0; i < people.length; i++) {
             for (let j = i + 1; j < people.length; j++) {
               const link: GraphLink = {
                 source: `person-${people[i]}`,
                 target: `person-${people[j]}`,
                 type: 'co-location',
                 strength: 1,
                 label: `Co-located at ${location}`
               };
               links.push(link);
             }
           }
         } else if (people.length > 4) {
           // For large groups, only connect the first few people to reduce clutter
           for (let i = 0; i < Math.min(3, people.length); i++) {
             for (let j = i + 1; j < Math.min(4, people.length); j++) {
               const link: GraphLink = {
                 source: `person-${people[i]}`,
                 target: `person-${people[j]}`,
                 type: 'co-location',
                 strength: 1,
                 label: `Co-located at ${location}`
               };
               links.push(link);
             }
           }
         }
       });
     });

    // Generate movement links
    const filteredMovements = movements.filter(movement => {
      if (movement.caseId && movement.caseId !== currentCaseId) return false;
      const movementTime = new Date(movement.time).getTime();
      const startTime = getTimestampFromSlider(startTimeRange);
      const endTime = getTimestampFromSlider(endTimeRange);
      return movementTime >= startTime && movementTime <= endTime;
    });

    filteredMovements.forEach(movement => {
      const fromLocation = movement.fromLocation.global?.name || movement.fromLocation.local?.name;
      const toLocation = movement.toLocation.global?.name || movement.toLocation.local?.name;
      
      if (fromLocation && toLocation) {
        const link: GraphLink = {
          source: `location-${fromLocation}`,
          target: `location-${toLocation}`,
          type: 'movement',
          strength: 2,
          label: `${movement.person} moved`
        };
        links.push(link);
      }
    });

    // Generate evidence-location links
    filteredEvidence.forEach(evidence => {
      const locationName = evidence.location.global?.name || evidence.location.local?.name;
      if (locationName) {
        const link: GraphLink = {
          source: `evidence-${evidence.id}`,
          target: `location-${locationName}`,
          type: 'evidence-location',
          strength: 1,
          label: 'Found at'
        };
        links.push(link);
      }
    });

    // Generate family links (people with same last name or in same group)
    const familyGroups = new Map<string, string[]>();
    uniquePeople.forEach(person => {
      const lastName = person.split(' ').pop() || '';
      if (!familyGroups.has(lastName)) {
        familyGroups.set(lastName, []);
      }
      familyGroups.get(lastName)!.push(person);
    });

    familyGroups.forEach((members, family) => {
      if (members.length > 1) {
        for (let i = 0; i < members.length; i++) {
          for (let j = i + 1; j < members.length; j++) {
            const link: GraphLink = {
              source: `person-${members[i]}`,
              target: `person-${members[j]}`,
              type: 'family',
              strength: 3,
              label: 'Family'
            };
            links.push(link);
          }
        }
      }
    });

    return { nodes, links };
  }, [filteredSceneObjects, filteredEvidence, movements, currentCaseId, startTimeRange, endTimeRange, getTimestampFromSlider]);

  // Filter nodes and links based on graph type and relationship type
  const filteredNodes = useMemo(() => {
    let filtered = nodes;
    
    if (graphType !== 'all') {
      filtered = filtered.filter(node => node.type === graphType);
    }
    
    // Apply simplification if enabled
    if (simplifyMode) {
      // Count connections for each node
      const connectionCounts = new Map<string, number>();
      links.forEach(link => {
        connectionCounts.set(link.source, (connectionCounts.get(link.source) || 0) + 1);
        connectionCounts.set(link.target, (connectionCounts.get(link.target) || 0) + 1);
      });
      
      // Only keep nodes with minimum connections
      filtered = filtered.filter(node => {
        const connections = connectionCounts.get(node.id) || 0;
        return connections >= minConnections;
      });
    }
    
    return filtered;
  }, [nodes, graphType, simplifyMode, minConnections, links]);

  const filteredLinks = useMemo(() => {
    let filtered = links;
    
    if (relationshipType !== 'all') {
      filtered = filtered.filter(link => link.type === relationshipType);
    }
    
    // Only include links where both source and target nodes are in filteredNodes
    const nodeIds = new Set(filteredNodes.map(node => node.id));
    filtered = filtered.filter(link => 
      nodeIds.has(link.source) && nodeIds.has(link.target)
    );
    
    // Apply simplification if enabled
    if (simplifyMode) {
      // Remove duplicate links between same nodes (keep strongest)
      const linkMap = new Map<string, GraphLink>();
      filtered.forEach(link => {
        const key = [link.source, link.target].sort().join('-');
        const existing = linkMap.get(key);
        if (!existing || link.strength > existing.strength) {
          linkMap.set(key, link);
        }
      });
      filtered = Array.from(linkMap.values());
      
      // Only show strongest relationships
      filtered.sort((a, b) => b.strength - a.strength);
      filtered = filtered.slice(0, Math.min(filtered.length, 20)); // Limit to top 20 relationships
    }
    
    return filtered;
  }, [links, filteredNodes, relationshipType, simplifyMode]);

  // Force-directed layout simulation with better spacing
  const layoutNodes = useMemo(() => {
    const layoutedNodes = [...filteredNodes];
    
    // Initialize positions in a larger circle with better spacing
    const centerX = 500;
    const centerY = 350;
    const radius = Math.max(300, Math.sqrt(layoutedNodes.length) * 50); // Dynamic radius based on node count
    
    layoutedNodes.forEach((node, index) => {
      const angle = (index / layoutedNodes.length) * 2 * Math.PI;
      node.x = centerX + Math.cos(angle) * radius;
      node.y = centerY + Math.sin(angle) * radius;
    });
    
    // Enhanced force simulation with better parameters
    const iterations = 100; // More iterations for better layout
    const repulsion = 150; // Increased repulsion force
    const attraction = 0.05; // Reduced attraction for more spread
    const damping = 0.8; // Damping factor to prevent oscillation
    
    for (let iter = 0; iter < iterations; iter++) {
      // Repulsion between all nodes
      for (let i = 0; i < layoutedNodes.length; i++) {
        for (let j = i + 1; j < layoutedNodes.length; j++) {
          const nodeA = layoutedNodes[i];
          const nodeB = layoutedNodes[j];
          
          if (!nodeA.x || !nodeA.y || !nodeB.x || !nodeB.y) continue;
          
          const dx = nodeB.x - nodeA.x;
          const dy = nodeB.y - nodeA.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance > 0) {
            // Stronger repulsion for closer nodes
            const force = repulsion / (distance * distance);
            const fx = (dx / distance) * force;
            const fy = (dy / distance) * force;
            
            nodeA.x -= fx * damping;
            nodeA.y -= fy * damping;
            nodeB.x += fx * damping;
            nodeB.y += fy * damping;
          }
        }
      }
      
      // Attraction for connected nodes
      filteredLinks.forEach(link => {
        const sourceNode = layoutedNodes.find(n => n.id === link.source);
        const targetNode = layoutedNodes.find(n => n.id === link.target);
        
        if (!sourceNode || !targetNode || !sourceNode.x || !sourceNode.y || !targetNode.x || !targetNode.y) return;
        
        const dx = targetNode.x - sourceNode.x;
        const dy = targetNode.y - sourceNode.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance > 0) {
          const force = distance * attraction;
          const fx = (dx / distance) * force;
          const fy = (dy / distance) * force;
          
          sourceNode.x += fx * damping;
          sourceNode.y += fy * damping;
          targetNode.x -= fx * damping;
          targetNode.y -= fy * damping;
        }
      });
      
      // Keep nodes within larger bounds
      layoutedNodes.forEach(node => {
        if (!node.x || !node.y) return;
        node.x = Math.max(100, Math.min(900, node.x));
        node.y = Math.max(100, Math.min(600, node.y));
      });
    }
    
    return layoutedNodes;
  }, [filteredNodes, filteredLinks]);

  const handleNodeClick = (nodeId: string) => {
    setSelectedNode(selectedNode === nodeId ? null : nodeId);
    const node = nodes.find(n => n.id === nodeId);
    if (node && node.data && 'id' in node.data) {
      onObjectClick(node.data as SceneObject | Evidence);
    }
  };

  const handleNodeHover = (nodeId: string | null) => {
    setHoveredNode(nodeId);
  };

  // Get connected nodes for highlighting
  const getConnectedNodes = (nodeId: string): Set<string> => {
    const connected = new Set<string>();
    filteredLinks.forEach(link => {
      if (link.source === nodeId) {
        connected.add(link.target);
      } else if (link.target === nodeId) {
        connected.add(link.source);
      }
    });
    return connected;
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev * 1.2, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev / 1.2, 0.3));
  const handleCenter = () => setZoom(1);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Controls */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Graph Type</InputLabel>
            <Select
              value={graphType}
              onChange={(e) => setGraphType(e.target.value as any)}
              label="Graph Type"
            >
              <MenuItem value="all">All Entities</MenuItem>
              <MenuItem value="people">People Only</MenuItem>
              <MenuItem value="locations">Locations Only</MenuItem>
              <MenuItem value="evidence">Evidence Only</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Relationship Type</InputLabel>
            <Select
              value={relationshipType}
              onChange={(e) => setRelationshipType(e.target.value as any)}
              label="Relationship Type"
            >
              <MenuItem value="all">All Relationships</MenuItem>
              <MenuItem value="co-location">Co-location</MenuItem>
              <MenuItem value="movement">Movement</MenuItem>
              <MenuItem value="evidence-location">Evidence-Location</MenuItem>
              <MenuItem value="family">Family</MenuItem>
            </Select>
          </FormControl>

                     <Box sx={{ display: 'flex', gap: 1 }}>
             <Tooltip title="Zoom In">
               <IconButton size="small" onClick={handleZoomIn}>
                 <ZoomInIcon />
               </IconButton>
             </Tooltip>
             <Tooltip title="Zoom Out">
               <IconButton size="small" onClick={handleZoomOut}>
                 <ZoomOutIcon />
               </IconButton>
             </Tooltip>
             <Tooltip title="Center View">
               <IconButton size="small" onClick={handleCenter}>
                 <CenterIcon />
               </IconButton>
             </Tooltip>
             <Tooltip title="Help & Tutorial">
               <IconButton size="small" onClick={() => setTutorialOpen(true)}>
                 <HelpIcon />
               </IconButton>
             </Tooltip>
           </Box>

                     <FormControlLabel
             control={
               <Checkbox
                 checked={showLabels}
                 onChange={(e) => setShowLabels(e.target.checked)}
                 size="small"
               />
             }
             label="Show Labels"
           />

           <FormControlLabel
             control={
               <Checkbox
                 checked={simplifyMode}
                 onChange={(e) => setSimplifyMode(e.target.checked)}
                 size="small"
               />
             }
             label="Simplify Graph"
           />
                 </Box>

         {simplifyMode && (
           <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
             <Typography variant="body2" sx={{ minWidth: 120 }}>
               Min Connections: {minConnections}
             </Typography>
             <Slider
               value={minConnections}
               onChange={(_, value) => setMinConnections(value as number)}
               min={1}
               max={5}
               step={1}
               marks
               valueLabelDisplay="auto"
               sx={{ flex: 1, maxWidth: 200 }}
             />
           </Box>
         )}

         <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            icon={<PersonIcon />}
            label={`${filteredNodes.filter(n => n.type === 'person').length} People`}
            size="small"
            color="success"
          />
          <Chip
            icon={<LocationIcon />}
            label={`${filteredNodes.filter(n => n.type === 'location').length} Locations`}
            size="small"
            color="primary"
          />
          <Chip
            icon={<EvidenceIcon />}
            label={`${filteredNodes.filter(n => n.type === 'evidence').length} Evidence`}
            size="small"
            color="warning"
          />
          <Chip
            icon={<GraphIcon />}
            label={`${filteredLinks.length} Relationships`}
            size="small"
            color="info"
          />
        </Box>
      </Box>

      {/* Graph Visualization */}
      <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden', backgroundColor: '#f8f9fa' }}>
        <svg
          width="100%"
          height="100%"
          style={{ transform: `scale(${zoom})`, transformOrigin: 'center' }}
        >
                     {/* Render links */}
           {filteredLinks.map((link, index) => {
             const sourceNode = layoutNodes.find(n => n.id === link.source);
             const targetNode = layoutNodes.find(n => n.id === link.target);
             
             if (!sourceNode || !targetNode || !sourceNode.x || !sourceNode.y || !targetNode.x || !targetNode.y) {
               return null;
             }

             const linkColors = {
               'co-location': '#4CAF50',
               'movement': '#2196F3',
               'evidence-location': '#FF9800',
               'family': '#9C27B0',
               'case': '#607D8B'
             };

                           // Highlight links connected to hovered node
              const isConnectedToHovered = hoveredNode && (link.source === hoveredNode || link.target === hoveredNode);
              const opacity = isConnectedToHovered ? 1 : 0.4; // Reduced default opacity
              const strokeWidth = isConnectedToHovered ? link.strength + 1 : link.strength;

             return (
               <g key={`link-${index}`}>
                 <line
                   x1={sourceNode.x}
                   y1={sourceNode.y}
                   x2={targetNode.x}
                   y2={targetNode.y}
                   stroke={linkColors[link.type] || '#999'}
                   strokeWidth={strokeWidth}
                   opacity={opacity}
                 />
                 {showLabels && link.label && (
                   <text
                     x={(sourceNode.x + targetNode.x) / 2}
                     y={(sourceNode.y + targetNode.y) / 2}
                     textAnchor="middle"
                     fontSize="10"
                     fill="#666"
                     dy="-5"
                   >
                     {link.label}
                   </text>
                 )}
               </g>
             );
           })}

                     {/* Render nodes */}
           {layoutNodes.map((node) => {
             if (!node.x || !node.y) return null;

             const isSelected = selectedNode === node.id;
             const isHovered = hoveredNode === node.id;
             const connectedNodes = hoveredNode ? getConnectedNodes(hoveredNode) : new Set();
             const isConnected = connectedNodes.has(node.id);
             
             const nodeColors = {
               person: '#4CAF50',
               location: '#2196F3',
               evidence: '#FF9800'
             };

             // Generate tooltip content
             let tooltipContent = node.label;
             if (node.type === 'person') {
               const appearances = filteredSceneObjects.filter(obj => obj.people.includes(node.data.name)).length;
               tooltipContent = `${node.label}\nAppearances: ${appearances}`;
             } else if (node.type === 'location') {
               const peopleCount = filteredSceneObjects.filter(obj => 
                 (obj.location.global?.name === node.data.name || obj.location.local?.name === node.data.name)
               ).length;
               const evidenceCount = filteredEvidence.filter(evidence => 
                 (evidence.location.global?.name === node.data.name || evidence.location.local?.name === node.data.name)
               ).length;
               tooltipContent = `${node.label}\nPeople: ${peopleCount} | Evidence: ${evidenceCount}`;
             } else if (node.type === 'evidence') {
               const evidence = node.data as Evidence;
               tooltipContent = `${evidence.name}\nType: ${evidence.type}\nTime: ${new Date(evidence.time).toLocaleString()}`;
             }

             return (
               <g key={node.id}>
                 <circle
                   cx={node.x}
                   cy={node.y}
                   r={node.size}
                   fill={nodeColors[node.type]}
                   stroke={isSelected ? '#000' : isHovered ? '#FFD700' : isConnected ? '#FFA500' : '#fff'}
                   strokeWidth={isSelected ? 3 : isHovered || isConnected ? 2 : 1}
                   style={{ cursor: 'pointer' }}
                   onClick={() => handleNodeClick(node.id)}
                   onMouseEnter={() => handleNodeHover(node.id)}
                   onMouseLeave={() => handleNodeHover(null)}
                 />
                 {showLabels && (
                   <text
                     x={node.x}
                     y={node.y + node.size + 15}
                     textAnchor="middle"
                     fontSize="12"
                     fontWeight="bold"
                     fill="#333"
                     style={{ pointerEvents: 'none' }}
                   >
                     {node.label}
                   </text>
                 )}
                 {/* Tooltip */}
                 <title>{tooltipContent}</title>
               </g>
             );
           })}
        </svg>

        {/* Summary Panel */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            backgroundColor: 'white',
            borderRadius: 1,
            p: 2,
            boxShadow: 2,
            minWidth: 250
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Relationship Summary
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2">
              <strong>Co-locations:</strong> {filteredLinks.filter(l => l.type === 'co-location').length}
            </Typography>
            <Typography variant="body2">
              <strong>Movements:</strong> {filteredLinks.filter(l => l.type === 'movement').length}
            </Typography>
            <Typography variant="body2">
              <strong>Evidence-Location:</strong> {filteredLinks.filter(l => l.type === 'evidence-location').length}
            </Typography>
            <Typography variant="body2">
              <strong>Family:</strong> {filteredLinks.filter(l => l.type === 'family').length}
            </Typography>
            {hoveredNode && (
              <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Connected to: {getConnectedNodes(hoveredNode).size} nodes
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Legend */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            backgroundColor: 'white',
            borderRadius: 1,
            p: 2,
            boxShadow: 2,
            minWidth: 200
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Legend
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#4CAF50' }} />
              <Typography variant="body2">People</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#2196F3' }} />
              <Typography variant="body2">Locations</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#FF9800' }} />
              <Typography variant="body2">Evidence</Typography>
            </Box>
          </Box>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            Relationships
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 2, backgroundColor: '#4CAF50' }} />
              <Typography variant="body2">Co-location</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 2, backgroundColor: '#2196F3' }} />
              <Typography variant="body2">Movement</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 2, backgroundColor: '#FF9800' }} />
              <Typography variant="body2">Evidence-Location</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 2, backgroundColor: '#9C27B0' }} />
              <Typography variant="body2">Family</Typography>
            </Box>
          </Box>
                 </Box>
       </Box>

       {/* Quick Start Guide */}
       {showQuickStart && (
         <Box
           sx={{
             position: 'absolute',
             top: '50%',
             left: '50%',
             transform: 'translate(-50%, -50%)',
             backgroundColor: 'white',
             borderRadius: 2,
             p: 3,
             boxShadow: 3,
             maxWidth: 400,
             zIndex: 1000
           }}
         >
           <Typography variant="h6" gutterBottom>
             Welcome to the Relationship Graph! 🕵️‍♂️
           </Typography>
           <Typography paragraph>
             This view helps you discover connections between people, locations, and evidence in your investigation.
           </Typography>
           
           <Box sx={{ mb: 2 }}>
             <Typography variant="subtitle2" gutterBottom>
               <strong>Quick Start:</strong>
             </Typography>
             <List dense>
               <ListItem>
                 <ListItemIcon>
                   <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#4CAF50' }} />
                 </ListItemIcon>
                 <ListItemText primary="Green circles = People" />
               </ListItem>
               <ListItem>
                 <ListItemIcon>
                   <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#2196F3' }} />
                 </ListItemIcon>
                 <ListItemText primary="Blue circles = Locations" />
               </ListItem>
               <ListItem>
                 <ListItemIcon>
                   <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#FF9800' }} />
                 </ListItemIcon>
                 <ListItemText primary="Orange circles = Evidence" />
               </ListItem>
             </List>
           </Box>

           <Typography variant="body2" sx={{ mb: 2 }}>
             <strong>Try:</strong> Hover over nodes to see connections, use filters to focus on specific relationships, 
             and click the help button (?) for a detailed tutorial.
           </Typography>

           <Box sx={{ display: 'flex', gap: 1 }}>
             <Button 
               variant="contained" 
               onClick={() => setShowQuickStart(false)}
               size="small"
             >
               Got it!
             </Button>
             <Button 
               variant="outlined" 
               onClick={() => {
                 setShowQuickStart(false);
                 setTutorialOpen(true);
               }}
               size="small"
             >
               Show Tutorial
             </Button>
           </Box>
         </Box>
       )}

       {/* Tutorial Dialog */}
       <RelationshipGraphTutorial
         open={tutorialOpen}
         onClose={() => setTutorialOpen(false)}
       />
     </Box>
   );
 };

export default RelationshipGraph;
